import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import type {
  ComplianceGap,
  CompliancePosture,
  GapSeverity,
  RecommendationGuidance,
  RecommendationRiskLevel,
  SnapshotGap,
} from '@lizrd/contracts';
import { NIS2_CONTROL_BY_ID } from '@lizrd/contracts';
import { generateComplianceFix, getComplianceSummary, getComplianceTrend } from '../lib/api';
import { LineChart } from '../components/ui/line-chart';
import { Select } from '../components/ui/select';
import { relativeTime } from '../lib/graph-visuals';
import { Card } from '../components/ui/card';
import { DiffBlock } from '../components/ui/diff-block';
import { InlineMarkdown, MarkdownLite } from '../components/ui/markdown-lite';
import { Pill } from '../components/ui/pill';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Modal } from '../components/ui/modal';
import { Progress } from '../components/ui/progress';
import { Stat } from '../components/ui/stat';
import { Skeleton } from '../components/ui/skeleton';
import { Spinner } from '../components/ui/spinner';

const SEVERITY_BADGE: Record<GapSeverity, 'danger' | 'warning' | 'info'> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
};

/** 0–100 → bar color: strong = success, mid = warning, weak = danger. */
function scoreVariant(score: number): 'success' | 'warning' | 'danger' {
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'danger';
}

/** Plain-language band for a 0–100 posture score. */
function scoreBand(score: number): { label: string; hint: string } {
  if (score >= 90) return { label: 'strong', hint: 'few or no open gaps' };
  if (score >= 70) return { label: 'fair', hint: 'a handful of gaps to close' };
  if (score >= 50)
    return {
      label: 'needs attention',
      hint: 'several controls are failing, including critical ones',
    };
  return { label: 'at risk', hint: 'critical controls are widely unmet' };
}

/** Gaps shown per page on the Compliance page. */
const PAGE_SIZE = 10;

/**
 * The NIS2 compliance lens for a domain (G3/G5): the posture score, the per-control
 * breakdown, the ranked gap list, and the per-domain policy. Posture is computed
 * live from the knowledge graph — "evidence & continuous posture for the cloud
 * technical measures", not a certification. Shown only when the `compliance`
 * feature flag is on (the nav tab is gated); this page handles the "not yet
 * enabled on this domain" state.
 */
export function CompliancePage() {
  const { domainId = '' } = useParams();

  const summary = useQuery({
    queryKey: ['compliance', domainId],
    queryFn: () => getComplianceSummary(domainId),
    enabled: !!domainId,
  });

  if (summary.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const posture = summary.data?.posture;
  const gaps = summary.data?.gaps ?? [];

  // Off for this domain → the educational state; it's enabled in Domain Settings.
  if (!posture?.enabled) return <ComplianceDisabled domainId={domainId} />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted">
          Evidence &amp; continuous posture for your cloud <b className="text-bone">technical</b>{' '}
          NIS2 measures — computed live from the knowledge graph. Not a certification; not legal
          advice.
        </p>
        <Link
          to={`/domains/${domainId}/compliance/evidence`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex flex-none items-center gap-1.5 rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm font-semibold text-bone hover:border-iris hover:text-iris"
        >
          Export evidence pack →
        </Link>
      </div>

      <PostureSummary posture={posture} gapCount={gaps.length} />

      <PostureTrend domainId={domainId} />

      <GapList gaps={gaps} domainId={domainId} />
    </div>
  );
}

/** Shown when NIS2 compliance is off for the domain: explains the capability and
 * links to the settings section (which auto-scrolls to it) to turn it on. */
function ComplianceDisabled({ domainId }: { domainId: string }) {
  return (
    <Card className="p-8">
      <div className="mx-auto max-w-xl text-center">
        <ShieldIcon className="mx-auto h-10 w-10 text-viridian" />
        <h3 className="mt-3 font-display text-lg font-bold text-bone">
          Prove your cloud meets NIS2 — continuously
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          NIS2 (EU directive 2022/2555) requires in-scope organisations to continuously maintain
          technical risk-management measures across their estate. When enabled, Lizrd maps the
          cloud-technical slice of those §30 measures to your knowledge graph — a posture score, a
          ranked gap list, and compliance guardrails on every cost recommendation. Evidence and
          posture for the technical measures, not a certification; not legal advice.
        </p>
        <div className="mx-auto mt-5 grid max-w-md gap-2.5 text-left">
          <Feature
            title="Live posture score"
            desc="Your estate scored against the NIS2 §30 measures, re-checked on every sync."
          />
          <Feature
            title="Ranked gaps with remediation"
            desc="Unencrypted stores, public exposure, missing backups, EOL engines — highest-risk first."
          />
          <Feature
            title="Guardrails on cost actions"
            desc="Lizrd flags a saving that would weaken a control and offers the compliant alternative."
          />
        </div>
        <Link
          to={`/domains/${domainId}/settings#compliance`}
          className="mt-6 inline-block rounded-sm bg-iris px-4 py-2 text-sm font-semibold text-on-iris transition-colors hover:bg-iris-strong"
        >
          Enable NIS2 compliance in Settings →
        </Link>
      </div>
    </Card>
  );
}

function Feature({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <svg
        aria-hidden
        className="mt-0.5 h-4 w-4 flex-none text-viridian"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
      >
        <path d="M5 12l5 5 9-11" />
      </svg>
      <div>
        <div className="text-sm font-semibold text-bone">{title}</div>
        <div className="text-xs leading-relaxed text-muted">{desc}</div>
      </div>
    </div>
  );
}

/** The top summary: the posture score + what it means (left) and the per-control
 * breakdown (right), combined into one section. */
function PostureSummary({ posture, gapCount }: { posture: CompliancePosture; gapCount: number }) {
  const score = posture.score;
  const band = scoreBand(score);
  return (
    <Card className="grid gap-6 p-6 lg:grid-cols-[minmax(0,21rem)_1fr]">
      {/* Score + the key numbers; the explanation sits below. */}
      <div>
        <div className="flex items-center gap-5">
          <div
            className="grid h-32 w-32 flex-none place-items-center rounded-full"
            style={{ background: `conic-gradient(var(--viridian) ${score}%, var(--surface-3) 0)` }}
            role="img"
            aria-label={`NIS2 posture score ${score} of 100`}
          >
            <div className="grid h-[104px] w-[104px] place-items-center rounded-full bg-surface text-center shadow-card">
              <div>
                <div className="font-display text-4xl font-bold leading-none">{score}</div>
                <div className="mt-1 font-mono text-[0.62rem] text-muted">/ 100</div>
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-lg font-bold">NIS2 posture</h2>
            <Badge variant={scoreVariant(score)} className="mt-1.5">
              {band.label}
            </Badge>
          </div>
        </div>
        <div className="mt-5 flex divide-x divide-hairline">
          <Stat
            className="flex-1 pr-4 first:pl-0"
            value={<span className={gapCount > 0 ? 'text-danger' : undefined}>{gapCount}</span>}
            label="Open gaps"
          />
          <Stat
            className="flex-1 px-4"
            value={
              <span className={posture.criticalGapCount > 0 ? 'text-danger' : undefined}>
                {posture.criticalGapCount}
              </span>
            }
            label="Critical"
          />
          <Stat className="flex-1 px-4" value={posture.assetsInScope} label="In scope" />
        </div>
        <p className="mt-4 text-sm text-muted">
          Weighted share of NIS2 control checks your in-scope resources pass — 100 means all pass;
          it falls as gaps appear.
        </p>
      </div>

      {/* Per-control breakdown */}
      <div className="lg:border-l lg:border-hairline lg:pl-6">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-semibold">Per-control posture</h3>
          <span className="font-mono text-[0.62rem] text-muted">
            share of applicable resources met
          </span>
        </div>
        {posture.controls.length === 0 ? (
          <p className="mt-2 text-sm text-muted">
            No applicable resources for the enforced controls yet.
          </p>
        ) : (
          <div className="mt-1.5 divide-y divide-hairline">
            {posture.controls.map((c) => {
              const applicable = c.met + c.gaps;
              return (
                <div key={c.controlId} className="flex items-center gap-3 py-2">
                  <div className="w-40 flex-none">
                    <div className="truncate text-sm font-medium" title={c.label}>
                      {c.label}
                    </div>
                    <div
                      className="truncate font-mono text-[0.6rem] text-muted"
                      title={c.nis2Measure}
                    >
                      {c.nis2Measure}
                    </div>
                  </div>
                  <Progress className="flex-1" value={c.score} variant={scoreVariant(c.score)} />
                  <div className="w-14 flex-none text-right">
                    <div className="font-mono text-xs font-bold tabular-nums">{c.score}%</div>
                    <div className="font-mono text-[0.58rem] text-muted tabular-nums">
                      {c.met}/{applicable}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

/** Short "MMM d" for a snapshot timestamp. */
function fmtDay(iso: string | null): string {
  if (!iso) return 'the last check';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function deltaTone(delta: number): string {
  return delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-muted';
}

/** The "continuous" layer (G6 inc2 / G7): the posture score trend sparkline from the
 * append-only snapshot history + a drift feed of what changed since the last check,
 * plus a one-click immutable snapshot capture. */
type TrendRange = '30' | '60' | '90' | 'all';
const TREND_RANGES = [
  { value: '30' as const, label: 'Last 30 days' },
  { value: '60' as const, label: 'Last 60 days' },
  { value: '90' as const, label: 'Last 90 days' },
  { value: 'all' as const, label: 'All time' },
];

/** The "continuous" layer (G6 inc2 / G7): a daily posture-score trend over a
 * selectable window, plus a "what changed since your last check" feed. New gaps are
 * surfaced as "New" badges in the gaps list below (not duplicated here); this feed
 * focuses on the score, a pointer to those new gaps, and the resolved improvements
 * (compact list, capped, with a "see all" modal). */
function PostureTrend({ domainId }: { domainId: string }) {
  const [range, setRange] = useState<TrendRange>('30');
  const [resolvedOpen, setResolvedOpen] = useState(false);
  const trend = useQuery({
    queryKey: ['compliance-trend', domainId],
    queryFn: () => getComplianceTrend(domainId),
    enabled: !!domainId,
  });

  if (trend.isLoading) return <Skeleton className="h-56 w-full" />;
  const series = trend.data?.series ?? [];
  const drift = trend.data?.drift;

  // Daily points within the window; dates normalized to YYYY-MM-DD so the chart's
  // day axis parses them (a full ISO string renders as "NaN").
  const windowLen = range === 'all' ? series.length : Number(range) + 1;
  const windowed = series.slice(Math.max(0, series.length - windowLen));
  const points = windowed.map((p) => ({ day: p.date.slice(0, 10), value: p.score }));
  const windowDelta =
    windowed.length >= 2 ? windowed[windowed.length - 1]!.score - windowed[0]!.score : null;

  const resolved = drift?.resolvedGaps ?? [];
  const newCount = drift?.newGaps.length ?? 0;

  return (
    <>
      <Card className="grid gap-6 p-6 lg:grid-cols-[minmax(0,28rem)_1fr]">
        {/* Daily score trend, windowed */}
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
              Posture trend
            </h3>
            <Select
              value={range}
              onChange={(v) => setRange(v)}
              ariaLabel="Trend window"
              size="sm"
              options={TREND_RANGES}
            />
          </div>
          {points.length >= 2 ? (
            <LineChart
              height={160}
              series={[{ key: 'score', label: 'NIS2 score', color: 'var(--viridian)', points }]}
              formatValue={(n) => `${Math.round(n)}`}
              yMax={100}
              yTicks={[0, 25, 50, 75, 100]}
              formatTick={(n) => `${n}`}
            />
          ) : (
            <div className="grid h-[150px] place-items-center rounded-sm bg-surface-2/40 text-xs text-muted">
              Not enough history in this window yet.
            </div>
          )}
          <div className="mt-1 text-[0.65rem] text-muted">
            {windowed.length} day{windowed.length === 1 ? '' : 's'}
            {windowDelta != null && (
              <>
                {' · '}
                <span className={deltaTone(windowDelta)}>
                  {windowDelta >= 0 ? '▲' : '▼'} {Math.abs(windowDelta)} pts
                </span>{' '}
                in window
              </>
            )}
          </div>
        </div>

        {/* What changed since the prior snapshot */}
        <div>
          <h3 className="mb-2 font-display text-sm font-semibold uppercase tracking-wide text-haze">
            What changed since your last check
          </h3>
          {!drift || !drift.hasPrior ? (
            <p className="text-sm text-muted">
              Baseline captured. Changes will appear here after the next check.
            </p>
          ) : newCount === 0 && resolved.length === 0 ? (
            <p className="text-sm text-success">
              No change since {fmtDay(drift.since)} — your enforced NIS2 controls held.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-muted">
                Since {fmtDay(drift.since)} ·{' '}
                <span className={deltaTone(drift.scoreDelta)}>
                  score {drift.scoreDelta >= 0 ? '+' : ''}
                  {drift.scoreDelta}
                </span>
              </div>
              {newCount > 0 && (
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-sm border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-haze">
                  <span className="font-semibold text-danger">
                    {newCount} new gap{newCount === 1 ? '' : 's'}
                  </span>
                  <span className="text-muted">— marked</span>
                  <span className="rounded-full bg-iris/15 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-iris">
                    New
                  </span>
                  <span className="text-muted">in the list below</span>
                </div>
              )}
              {resolved.length > 0 && (
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-semibold text-success">
                      {resolved.length} resolved
                    </span>
                    {resolved.length > 5 && (
                      <button
                        type="button"
                        onClick={() => setResolvedOpen(true)}
                        className="text-xs font-semibold text-iris hover:text-iris-strong"
                      >
                        See all {resolved.length} →
                      </button>
                    )}
                  </div>
                  <ResolvedList items={resolved.slice(0, 5)} />
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
      <ResolvedModal
        open={resolvedOpen}
        onClose={() => setResolvedOpen(false)}
        items={resolved}
        since={drift?.since ?? null}
      />
    </>
  );
}

/** A compact, structured list of changed gaps — control · resource · what was wrong.
 * Clean rows (no badges) so a long list stays scannable. */
function ResolvedList({ items }: { items: SnapshotGap[] }) {
  return (
    <ul className="divide-y divide-hairline/60 overflow-hidden rounded-sm border border-hairline">
      {items.map((g, i) => (
        <li key={i} className="flex items-center gap-3 px-3 py-1.5 text-xs">
          <span className="w-40 flex-none truncate font-medium text-haze">{g.controlLabel}</span>
          <span className="flex-none font-mono text-muted">{g.resourceName}</span>
          <span className="min-w-0 flex-1 truncate text-muted">{g.summary}</span>
        </li>
      ))}
    </ul>
  );
}

/** Resolved gaps shown per page in the "see all" modal. */
const RESOLVED_PAGE_SIZE = 10;

/** The "see all resolved" modal — a master/detail view: a paginated list of resolved
 * gaps whose rows open a READ-ONLY detail (mirrors the open-gap modal, clearly marked
 * Resolved). No actions — a resolved gap needs none. */
function ResolvedModal({
  open,
  onClose,
  items,
  since,
}: {
  open: boolean;
  onClose: () => void;
  items: SnapshotGap[];
  since: string | null;
}) {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<SnapshotGap | null>(null);
  // Reset to the first page / list view whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) {
      setPage(0);
      setSelected(null);
    }
  }, [open]);

  const pageCount = Math.max(1, Math.ceil(items.length / RESOLVED_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const shown = items.slice(safePage * RESOLVED_PAGE_SIZE, safePage * RESOLVED_PAGE_SIZE + RESOLVED_PAGE_SIZE); // prettier-ignore

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="3xl"
      title={
        selected ? (
          <div className="min-w-0">
            <div className="font-display text-base font-bold text-bone">{selected.summary}</div>
            <div className="text-[0.7rem] text-muted">Resolved NIS2 gap</div>
          </div>
        ) : (
          <div className="min-w-0">
            <div className="font-display text-base font-bold text-bone">
              Resolved since {fmtDay(since)}
            </div>
            <div className="text-[0.7rem] text-muted">
              {items.length} gap{items.length === 1 ? '' : 's'} no longer present
            </div>
          </div>
        )
      }
    >
      {selected ? (
        <ResolvedGapDetail gap={selected} since={since} onBack={() => setSelected(null)} />
      ) : (
        <div className="space-y-3">
          <ul className="divide-y divide-hairline/60 overflow-hidden rounded-md border border-hairline">
            {shown.map((g, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => setSelected(g)}
                  className="grid w-full grid-cols-[minmax(0,12rem)_5.5rem_minmax(0,10rem)_minmax(0,1fr)_1rem] items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-2"
                >
                  <span className="truncate text-sm font-medium text-bone">{g.controlLabel}</span>
                  <Badge variant={SEVERITY_BADGE[g.severity]} className="justify-self-start">
                    {g.severity}
                  </Badge>
                  <span className="truncate font-mono text-xs text-muted">{g.resourceName}</span>
                  <span className="truncate text-xs text-muted">{g.summary}</span>
                  <svg
                    aria-hidden
                    className="h-4 w-4 justify-self-end text-muted"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
          {pageCount > 1 && (
            <div className="flex items-center justify-between text-xs text-muted">
              <span>
                Page {safePage + 1} of {pageCount}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={safePage === 0}
                  onClick={() => setPage(safePage - 1)}
                  className="rounded-sm border border-hairline px-2 py-1 font-semibold text-haze hover:border-iris hover:text-iris disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage(safePage + 1)}
                  className="rounded-sm border border-hairline px-2 py-1 font-semibold text-haze hover:border-iris hover:text-iris disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Read-only detail for a resolved gap — mirrors the open-gap modal's information
 * (severity · resource · cloud · NIS2 type · what was wrong · what NIS2 expects) but
 * with a clear "Resolved" banner and NO remediation actions. */
function ResolvedGapDetail({
  gap,
  since,
  onBack,
}: {
  gap: SnapshotGap;
  since: string | null;
  onBack: () => void;
}) {
  const def = NIS2_CONTROL_BY_ID[gap.controlId];
  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-semibold text-iris hover:text-iris-strong"
      >
        ← Back to resolved list
      </button>

      <Alert variant="success">
        <div className="font-semibold text-bone">Resolved — no longer present</div>
        <div className="mt-0.5 text-sm text-haze">
          This gap is no longer detected in your cloud (resolved on or before {fmtDay(since)}). No
          action is required — it&apos;s shown for the audit trail.
        </div>
      </Alert>

      <dl className="grid grid-cols-[9rem_1fr] gap-x-4 gap-y-3 text-sm">
        <dt className="text-muted">Severity (when open)</dt>
        <dd>
          <Badge variant={SEVERITY_BADGE[gap.severity]}>{gap.severity}</Badge>
        </dd>

        <dt className="text-muted">Affected resource</dt>
        <dd className="min-w-0">
          <div className="font-medium text-bone">{gap.resourceName}</div>
          <div className="font-mono text-[0.68rem] text-muted">{gap.resourceType}</div>
        </dd>

        <dt className="text-muted">Cloud</dt>
        <dd className="text-bone">{gap.provider}</dd>

        <dt className="text-muted">NIS2 compliance type</dt>
        <dd>
          <div className="text-bone">{def.label}</div>
          <div className="font-mono text-[0.68rem] text-muted">{def.nis2Measure}</div>
        </dd>
      </dl>

      <div className="h-px bg-hairline" />

      <FixSection title="What was wrong">
        <p className="text-sm leading-relaxed text-haze">{gap.summary}</p>
      </FixSection>

      <FixSection title="What NIS2 expects">
        <p className="text-sm leading-relaxed text-haze">
          <b className="text-bone">{def.label}.</b> {def.description}
        </p>
      </FixSection>
    </div>
  );
}

function GapList({ gaps, domainId }: { gaps: ComplianceGap[]; domainId: string }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(gaps.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const shown = gaps.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Gaps — ranked by risk</h3>
        <span className="font-mono text-xs text-muted">{gaps.length} total</span>
      </div>
      <p className="mb-3 mt-1 text-sm text-muted">
        Each gap is one resource failing one NIS2 control, highest-risk first. Closing a gap raises
        your score — critical gaps move it most. Expand <b className="text-bone">How to fix</b> for
        the remediation.
      </p>
      {gaps.length === 0 ? (
        <Alert variant="success">
          <div className="font-semibold text-bone">No open gaps</div>
          <div className="mt-0.5">Every enforced control is met across the in-scope estate.</div>
        </Alert>
      ) : (
        <>
          <div className="divide-y divide-hairline">
            {shown.map((g) => (
              <GapRow key={g.id} gap={g} domainId={domainId} />
            ))}
          </div>
          {pageCount > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={current === 0}
                onClick={() => setPage(current - 1)}
              >
                Previous
              </Button>
              <span className="font-mono text-xs text-muted">
                Page {current + 1} of {pageCount} · showing {shown.length} of {gaps.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={current >= pageCount - 1}
                onClick={() => setPage(current + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function GapRow({ gap, domainId }: { gap: ComplianceGap; domainId: string }) {
  const [open, setOpen] = useState(false);
  const def = NIS2_CONTROL_BY_ID[gap.controlId];
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="-mx-2 flex w-full items-center gap-3 rounded-md px-2 py-3.5 text-left transition-colors hover:bg-surface-2"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {gap.isNew && (
              <span className="flex-none rounded-full bg-iris/15 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-iris">
                New
              </span>
            )}
            <span className="font-medium text-bone">{gap.summary}</span>
            <span className="font-mono text-xs text-muted">{gap.resourceName}</span>
            <Badge variant={SEVERITY_BADGE[gap.severity]} className="flex-none">
              {gap.severity}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-haze">
            <b className="text-bone">{def.label}:</b> {def.description}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[0.68rem] text-muted">
            <span>{gap.nis2Measure}</span>
            <span>·</span>
            <span>{gap.resourceType}</span>
            {gap.region && (
              <>
                <span>·</span>
                <span>{gap.region}</span>
              </>
            )}
            {gap.firstDetectedAt && (
              <>
                <span>·</span>
                <span>detected {relativeTime(gap.firstDetectedAt)}</span>
              </>
            )}
          </div>
        </div>
        <svg
          aria-hidden
          className="h-4 w-4 flex-none text-muted"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      </button>
      <GapFixModal gap={gap} domainId={domainId} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

const RISK_META: Record<RecommendationRiskLevel, { label: string; color: string }> = {
  low: { label: 'Low risk', color: 'var(--iris)' },
  medium: { label: 'Medium risk', color: 'var(--warning)' },
  high: { label: 'High risk', color: 'var(--danger)' },
};

/** A titled block inside the fix modal — identical styling to the optimization
 * guidance modal's Section, so the two read as siblings. */
function FixSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h4 className="mb-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
        {title}
      </h4>
      {children}
    </div>
  );
}

/** The generated verified fix — mirrors the optimization guidance modal's layout
 * (what the change is · risk · how to apply · the exact diff), markdown-rendered. */
function FixGuidance({ g }: { g: RecommendationGuidance }) {
  const risk = RISK_META[g.riskLevel];
  return (
    <>
      <FixSection title="The fix">
        <MarkdownLite text={g.actionExplanation} className="text-sm leading-relaxed text-haze" />
      </FixSection>

      <FixSection title="Risk">
        <div
          className="rounded-md border p-3"
          style={{ borderColor: `color-mix(in srgb, ${risk.color} 40%, transparent)` }}
        >
          <Pill color={risk.color}>{risk.label}</Pill>
          <p className="mt-1.5 text-sm leading-relaxed text-haze">
            <InlineMarkdown text={g.riskSummary} />
          </p>
        </div>
      </FixSection>

      {g.applySteps.length > 0 && (
        <FixSection title="How to apply it">
          <ol className="space-y-1.5">
            {g.applySteps.map((step, i) => (
              <li key={i} className="flex gap-2 text-sm leading-relaxed text-haze">
                <span className="grid h-5 w-5 flex-none place-items-center rounded-full bg-iris text-[0.65rem] font-bold text-on-iris">
                  {i + 1}
                </span>
                <span>
                  <InlineMarkdown text={step} />
                </span>
              </li>
            ))}
          </ol>
        </FixSection>
      )}

      {g.diff ? (
        <FixSection title="The exact change (diff)">
          <DiffBlock diff={g.diff} />
        </FixSection>
      ) : (
        <div className="rounded-md border border-hairline bg-surface-2/60 px-3 py-2 text-xs text-muted">
          Prose steps only — connect a code base for this domain and Lizrd grounds the fix in your
          IaC as a verified diff.
        </div>
      )}

      <p className="text-xs text-muted">
        Apply this in your IaC — Lizrd re-checks the control on the next sync and clears the gap
        once it&apos;s met.
      </p>
    </>
  );
}

/** The gap remediation modal — mirrors the optimization recommendation detail
 * modal: a titled dialog with the control, the gap, and the remediation. */
function GapFixModal({
  gap,
  domainId,
  open,
  onClose,
}: {
  gap: ComplianceGap;
  domainId: string;
  open: boolean;
  onClose: () => void;
}) {
  const def = NIS2_CONTROL_BY_ID[gap.controlId];
  // Generate the verified IaC fix on demand (grounded + validated server-side;
  // a deterministic demo fix in the sandbox). Only while the modal is open.
  const fix = useQuery({
    queryKey: ['compliance-fix', domainId, gap.id],
    queryFn: () => generateComplianceFix(domainId, gap.id),
    enabled: open,
    staleTime: Infinity,
    retry: false,
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="2xl"
      title={
        <div className="min-w-0">
          <div className="font-display text-base font-bold text-bone">{gap.summary}</div>
          <div className="text-[0.7rem] text-muted">NIS2 compliance gap</div>
        </div>
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          {gap.relatedRecommendationId ? (
            <Link
              to={`/domains/${domainId}/recommendations/${gap.relatedRecommendationId}`}
              onClick={onClose}
              className="text-xs font-semibold text-iris hover:text-iris-strong"
            >
              Related optimization on this resource →
            </Link>
          ) : (
            <span />
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <dl className="grid grid-cols-[8.5rem_1fr] gap-x-4 gap-y-3 text-sm">
          <dt className="text-muted">Severity</dt>
          <dd>
            <Badge variant={SEVERITY_BADGE[gap.severity]}>{gap.severity}</Badge>
          </dd>

          <dt className="text-muted">Affected resource</dt>
          <dd className="min-w-0">
            <div className="font-medium text-bone">{gap.resourceName}</div>
            <div className="font-mono text-[0.68rem] text-muted">{gap.resourceType}</div>
          </dd>

          <dt className="text-muted">Cloud &amp; region</dt>
          <dd className="text-bone">
            {gap.provider}
            {gap.region ? ` · ${gap.region}` : ''}
          </dd>

          <dt className="text-muted">NIS2 compliance type</dt>
          <dd>
            <div className="text-bone">{def.label}</div>
            <div className="font-mono text-[0.68rem] text-muted">{def.nis2Measure}</div>
          </dd>
        </dl>

        <div className="h-px bg-hairline" />

        <FixSection title="What NIS2 expects">
          <p className="text-sm leading-relaxed text-haze">
            <b className="text-bone">{def.label}.</b> {def.description}
          </p>
        </FixSection>

        {fix.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted">
            <Spinner size="sm" /> Generating a grounded fix…
          </div>
        ) : fix.data ? (
          <FixGuidance g={fix.data} />
        ) : (
          <FixSection title="Remediation">
            <p className="text-sm leading-relaxed text-haze">{gap.remediation}</p>
          </FixSection>
        )}
      </div>
    </Modal>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
    >
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
      <path d="M9.5 12l1.8 1.8L15 10" />
    </svg>
  );
}
